import { type PayloadAction, createSelector, createSlice } from "@reduxjs/toolkit";
import { sortBy, trim, trimEnd } from "lodash";
import { type RootState } from "@/core/store";
import {
  COMMAND_REG_EXP,
  ChatBrowserMessage,
  ChatTab,
  MAX_MESSAGES,
} from "@/domains/chat/constants/chat";
import { IS_DEV } from "@/domains/main/constants/other.ts";
import { tooltips } from "@/domains/chat/helpers/tooltips";

interface ISettings {
  isOpen: boolean;
}

export interface ChatState {
  tabs: ChatTab[];
  currTab: ChatTab;
  isShow: boolean;
  isExpanded: boolean;
  isActive: boolean;
  isEnabled: boolean;
  isOpen: boolean;
  isFastMessage: boolean;
  isBMode: boolean;
  isOpacityMode: boolean;
  message: string;
  messageCache: string;
  messages: ChatBrowserMessage[];
  messagesOoc: ChatBrowserMessage[];
  messagesFaction: ChatBrowserMessage[];
  messagesFamily: ChatBrowserMessage[];
  messagesNews: ChatBrowserMessage[];
  messagesRadio: ChatBrowserMessage[];
  messagesSystem: ChatBrowserMessage[];
  ownMessageHistory: string[];
  ownMessageHistoryPosition: number;
  tooltipCommands: { value: string; args?: string[]; description?: string }[];
  tooltipCommandsPosition: number;
  command: null | string;
  isOpenTooltipCommands: boolean;
  isCopiedMessage: boolean;
  dateChatWasClosed: Date | null;

  isOpenSearch: boolean;
  searchValue: string;
  indexMessageBySearch: number;
  uidMessagesBySearch: string[];

  settings: ISettings;
}

const initialState: ChatState = {
  tabs: [ChatTab.All, ChatTab.OOC, ChatTab.Faction, ChatTab.Family, ChatTab.News, ChatTab.Radio],
  currTab: ChatTab.All,
  isExpanded: false,
  isShow: false,
  isActive: true, // будет ли отображаться input
  isEnabled: false, // фокус на чате
  isOpen: false,
  isOpacityMode: true,
  isFastMessage: false, // если пользователь нажмет Т, когда чат будет свернут, то после отправки сообщения чат должен свернуться
  isBMode: true, // /b переключатель справа от поля ввода
  message: "",
  messages: [],
  messagesOoc: [],
  messagesFaction: [],
  messagesFamily: [],
  messagesNews: [],
  messagesRadio: [],
  messagesSystem: [],
  ownMessageHistory: [],
  ownMessageHistoryPosition: -1,
  messageCache: "",
  tooltipCommands: IS_DEV ? tooltips : [],
  tooltipCommandsPosition: -1,
  command: null,
  isOpenTooltipCommands: false,
  isCopiedMessage: false,
  dateChatWasClosed: new Date(),

  isOpenSearch: false,
  searchValue: "",
  indexMessageBySearch: -1, // текущий индекс из найденных сообщений 0 / 0
  uidMessagesBySearch: [], // индексы найденных сообщений из messages по searchValue

  settings: {
    isOpen: false,
  },
};

const chatSlice = createSlice({
  name: "chatSlice",
  initialState,
  reducers: {
    setChatActive: (state, action: PayloadAction<ChatState["isActive"]>) => {
      state.isActive = action.payload;
    },
    setChatExpand: (state, action: PayloadAction<ChatState["isExpanded"]>) => {
      state.isExpanded = action.payload;
    },
    setChatEnable: (state, action: PayloadAction<ChatState["isEnabled"]>) => {
      state.isEnabled = action.payload;
    },
    setChatShow: (state, action: PayloadAction<ChatState["isShow"]>) => {
      state.isShow = action.payload;
    },
    setChatOpen: (state, action: PayloadAction<ChatState["isOpen"]>) => {
      if (!action.payload) {
        state.dateChatWasClosed = new Date();
      }
      state.isOpen = action.payload;
    },
    setMessages: (
      state,
      action: PayloadAction<{ message: ChatState["messages"][0]; tab: ChatTab }>,
    ) => {
      const { message, tab } = action.payload;

      // if (!state.isOpen && state.settings.notifyInRolledUpMode) {
      //   callMessageNotification({
      //     message,
      //     settings: {
      //       ...state.settings,
      //     },
      //   });
      // }

      switch (tab) {
        case ChatTab.All: {
          state.messages = [message, ...state.messages.slice(0, MAX_MESSAGES)];
          break;
        }
        case ChatTab.OOC: {
          state.messagesOoc = [message, ...state.messagesOoc.slice(0, MAX_MESSAGES)];
          break;
        }
        case ChatTab.Faction: {
          state.messagesFaction = [message, ...state.messagesFaction.slice(0, MAX_MESSAGES)];
          break;
        }
        case ChatTab.Family: {
          state.messagesFamily = [message, ...state.messagesFamily.slice(0, MAX_MESSAGES)];
          break;
        }
        case ChatTab.News: {
          state.messagesNews = [message, ...state.messagesNews.slice(0, MAX_MESSAGES)];
          break;
        }
        case ChatTab.Radio: {
          state.messagesRadio = [message, ...state.messagesRadio.slice(0, MAX_MESSAGES)];
          break;
        }
        case ChatTab.System: {
          state.messagesSystem = [message, ...state.messagesSystem.slice(0, MAX_MESSAGES)];
          break;
        }
        default: {
          break;
        }
      }
    },
    resetMessages: (state) => {
      state.messages = [];
      state.messagesOoc = [];
      state.messagesFaction = [];
      state.messagesFamily = [];
      state.messagesNews = [];
      state.messagesRadio = [];
      state.messagesSystem = [];
    },
    setFastMessage: (state: ChatState, action: PayloadAction<boolean>) => {
      state.isFastMessage = action.payload;
    },
    setBMode: (state: ChatState, action: PayloadAction<boolean>) => {
      state.isBMode = action.payload;
    },
    setChatCommand: (state, action: PayloadAction<ChatState["command"]>) => {
      state.command = action.payload;
    },
    setOwnMessageHistory: (state, action: PayloadAction<ChatState["ownMessageHistory"]>) => {
      state.ownMessageHistory = action.payload;
    },
    setOwnMessageHistoryPosition: (
      state,
      action: PayloadAction<ChatState["ownMessageHistoryPosition"]>,
    ) => {
      state.ownMessageHistoryPosition = action.payload;
    },
    setMessageCache: (state, action: PayloadAction<ChatState["messageCache"]>) => {
      state.messageCache = action.payload;
    },
    setMessage: (state, action: PayloadAction<ChatState["message"]>) => {
      state.message = action.payload;
    },
    changeCurrTab: (state, action: PayloadAction<ChatState["currTab"]>) => {
      state.currTab = action.payload;
    },
    setSettingsOpen: (state, action: PayloadAction<ChatState["settings"]["isOpen"]>) => {
      state.settings.isOpen = action.payload;
    },
    setTooltipCommands: (state, action: PayloadAction<ChatState["tooltipCommands"]>) => {
      state.tooltipCommands = sortBy(action.payload, (item) => item);
    },
    setTooltipCommandsOpen: (state, action: PayloadAction<ChatState["isOpenTooltipCommands"]>) => {
      state.isOpenTooltipCommands = action.payload;
    },
    setTooltipCommandsPosition: (
      state,
      action: PayloadAction<ChatState["tooltipCommandsPosition"]>,
    ) => {
      state.tooltipCommandsPosition = action.payload;
    },
    setMessageIsCopied: (state, action: PayloadAction<ChatState["isCopiedMessage"]>) => {
      state.isCopiedMessage = action.payload;
    },
    setOpacityMode: (state, action: PayloadAction<boolean>) => {
      state.isOpacityMode = action.payload;
    },
    setOpenSearch: (state, action: PayloadAction<boolean>) => {
      state.isOpenSearch = action.payload;
    },
    setSearchValue: (state, action: PayloadAction<ChatState["searchValue"]>) => {
      state.searchValue = action.payload;
    },
    setIndexMessageBySearch: (state, action: PayloadAction<number>) => {
      state.indexMessageBySearch = action.payload;
    },
    setUidMessagesBySearch: (state, action: PayloadAction<ChatState["uidMessagesBySearch"]>) => {
      state.uidMessagesBySearch = [...action.payload];
    },
  },
});

// CHAT
export const tabsChatSelector = (state: RootState) => state.chat.tabs;
export const currTabChatSelector = (state: RootState) => state.chat.currTab;
export const chatIsExpandedSelector = (state: RootState) => state.chat.isExpanded;
export const chatIsActiveSelector = (state: RootState) => state.chat.isActive;

export const chatisShowSelector = (state: RootState) => state.chat.isShow;
export const chatIsEnabledSelector = (state: RootState) => state.chat.isEnabled;
export const chatIsOpenSelector = (state: RootState) => state.chat.isOpen;
export const chatIsOpacityModeSelector = (state: RootState) => state.chat.isOpacityMode;
export const messageSelector = (state: RootState) => state.chat.message;
export const messagesSelector = (state: RootState) => state.chat.messages;
export const messagesByTabSelector = createSelector(
  (state: RootState) => state.chat,
  (_, tab: ChatTab) => tab,
  (chat: ChatState, tab: ChatTab) => {
    switch (tab) {
      case ChatTab.All: {
        // TODO: фильтрация сообщений по настройкам общего чата
        return chat.messages;
      }
      case ChatTab.OOC: {
        return chat.messagesOoc;
      }
      case ChatTab.Faction: {
        return chat.messagesFaction;
      }
      case ChatTab.Family: {
        return chat.messagesFamily;
      }
      case ChatTab.News: {
        return chat.messagesNews;
      }
      case ChatTab.Radio: {
        return chat.messagesRadio;
      }
      default: {
        return chat.messages;
      }
    }
  },
);
export const ownMessageHistorySelector = (state: RootState) => state.chat.ownMessageHistory;
export const ownMessageHistoryPositionSelector = (state: RootState) =>
  state.chat.ownMessageHistoryPosition;
export const chatMessageCacheSelector = (state: RootState) => state.chat.messageCache;
export const chatCommandSelector = (state: RootState) => state.chat.command;
export const fastMessageSelector = (state: RootState) => state.chat.isFastMessage;
export const bModeSelector = (state: RootState) => state.chat.isBMode;
export const openTooltipCommandsSelector = (state: RootState) => state.chat.isOpenTooltipCommands;
export const tooltipCommandsAllSelector = (state: RootState) => state.chat.tooltipCommands;
export const dateChatWasClosedSelector = (state: RootState) => state.chat.dateChatWasClosed;
export const tooltipCommandsSelector = createSelector(
  (state: RootState) => state.chat.tooltipCommands,
  (_, message: string) => message,
  (tooltipCommands: ChatState["tooltipCommands"], message: string) => {
    let findedCommand: string | null = null;
    const matchCommand = trim(message).match(COMMAND_REG_EXP);
    if (matchCommand) {
      const [cmd] = matchCommand;
      findedCommand = trimEnd(cmd.replace("/", ""));
    }

    return tooltipCommands.filter((item) => item.value.startsWith(findedCommand)).slice(0, 5);
  },
);
export const tooltipCommandsPositionSelector = (state: RootState) =>
  state.chat.tooltipCommandsPosition;
export const messageIsCopiedSelector = (state: RootState) => state.chat.isCopiedMessage;

export const searchIsOpenSelector = (state: RootState) => state.chat.isOpenSearch;
export const searchValueSelector = (state: RootState) => state.chat.searchValue;
export const indexMessageBySearchSelector = (state: RootState) => state.chat.indexMessageBySearch;
export const uidMessagesBySearchSelector = (state: RootState) => state.chat.uidMessagesBySearch;
// SETTINGS
export const settingsSelector = (state: RootState) => state.chat.settings;

export const settingsIsOpenSelector = (state: RootState) => state.chat.settings.isOpen;

export const chatReducer = chatSlice.reducer;
export const {
  setChatActive,
  setChatShow,
  setChatCommand,
  setChatEnable,
  setChatExpand,
  setMessage,
  setMessageCache,
  setOwnMessageHistory,
  setOwnMessageHistoryPosition,
  setMessages,
  setChatOpen,
  changeCurrTab,
  setSettingsOpen,
  resetMessages,
  setFastMessage,
  setBMode,
  setTooltipCommandsOpen,
  setTooltipCommandsPosition,
  setMessageIsCopied,
  setTooltipCommands,
  setOpacityMode,
  setOpenSearch,
  setSearchValue,
  setIndexMessageBySearch,
  setUidMessagesBySearch,
} = chatSlice.actions;
